/**
 * Webhooks management page.
 */

import React, { useEffect, useState } from 'react';
import { getWebhooks, createWebhook, deleteWebhook, testWebhook } from '../services';
import { Webhook } from '../types';
import {
  Webhook as WebhookIcon,
  Plus,
  Trash2,
  Play,
  CheckCircle2,
  XCircle,
  Globe,
} from 'lucide-react';

const ALL_EVENTS = [
  'token.low_balance',
  'token.purchased',
  'token.depleted',
  'token.auto_renewed',
  'usage.limit_reached',
  'limit.exceeded',
  'payment.completed',
  'payment.failed',
  'auto_renew.paused',
] as const;

export default function WebhooksPage() {
  const [hooks, setHooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; ok: boolean } | null>(null);

  // form
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  const load = () => {
    getWebhooks()
      .then(setHooks)
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const toggleEvent = (ev: string) => {
    setEvents((prev) => (prev.includes(ev) ? prev.filter((e) => e !== ev) : [...prev, ev]));
  };

  const handleCreate = async () => {
    if (!url || events.length === 0) return;
    setCreating(true);
    await createWebhook(url, events);
    setCreating(false);
    setShowCreate(false);
    setUrl('');
    setEvents([]);
    load();
  };

  const handleDelete = async (id: string) => {
    await deleteWebhook(id);
    load();
  };

  const handleTest = async (id: string) => {
    setTesting(id);
    setTestResult(null);
    const ok = await testWebhook(id);
    setTestResult({ id, ok });
    setTesting(null);
  };

  if (loading) return <div className="page-loader">Loading webhooks…</div>;

  return (
    <div className="webhooks-page">
      <div className="page-header">
        <div>
          <h1>Webhooks</h1>
          <p>Receive real-time notifications for token and payment events</p>
        </div>
        <button className="btn btn-accent" onClick={() => setShowCreate(true)}>
          <Plus size={16} /> New Webhook
        </button>
      </div>

      {/* ── Create modal ─────────────────────────────────── */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Create Webhook</h2>
            <div className="form-group">
              <label>Endpoint URL</label>
              <input type="url" placeholder="https://api.example.com/webhooks" value={url} onChange={(e) => setUrl(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Events</label>
              <div className="checkbox-group">
                {ALL_EVENTS.map((ev) => (
                  <label key={ev} className="checkbox-label">
                    <input type="checkbox" checked={events.includes(ev)} onChange={() => toggleEvent(ev)} />
                    <code>{ev}</code>
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
              <button className="btn btn-accent" onClick={handleCreate} disabled={creating || !url || events.length === 0}>
                {creating ? 'Creating…' : 'Create Webhook'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Webhooks list ─────────────────────────────────── */}
      {hooks.length === 0 ? (
        <div className="empty-card">
          <WebhookIcon size={40} />
          <h3>No Webhooks</h3>
          <p>Create a webhook to start receiving event notifications.</p>
        </div>
      ) : (
        <div className="webhooks-list">
          {hooks.map((h) => (
            <div key={h.id} className="webhook-card">
              <div className="webhook-header">
                <div className="webhook-url">
                  <Globe size={16} />
                  <code>{h.url}</code>
                </div>
                <span className={`status-dot ${h.isActive ? 'active' : 'inactive'}`} />
              </div>
              <div className="webhook-events">
                {h.events.map((ev) => (
                  <span key={ev} className="event-tag">{ev}</span>
                ))}
              </div>
              <div className="webhook-meta">
                <span>Created {new Date(h.createdAt).toLocaleDateString()}</span>
                {h.lastTriggeredAt && <span>Last triggered {new Date(h.lastTriggeredAt).toLocaleDateString()}</span>}
              </div>
              <div className="webhook-actions">
                <button className="btn btn-ghost btn-sm" onClick={() => handleTest(h.id)} disabled={testing === h.id}>
                  <Play size={14} /> {testing === h.id ? 'Testing…' : 'Test'}
                </button>
                {testResult?.id === h.id && (
                  <span className={`test-result ${testResult.ok ? 'ok' : 'fail'}`}>
                    {testResult.ok ? <><CheckCircle2 size={14} /> 200 OK</> : <><XCircle size={14} /> Failed</>}
                  </span>
                )}
                <button className="btn btn-ghost btn-sm danger" onClick={() => handleDelete(h.id)}>
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
